import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Collapse,
  FormFeedback,
  Input,
  Label,
  Tooltip,
  UncontrolledCollapse,
} from 'reactstrap';

import { appState } from '../../AppState';
import ConnectriaTicketsService from '../../services/ConnectriaTicketsService';
import CookieService from '../../services/CookieService';
import PortalService from '../../services/PortalService';
import useAsyncEffect from '../../utility/use-async-effect';
import useTriaTicketsState, { createdBy } from './ConnectriaTicketsState';

import FileService from '../../services/FileService';
import YetiContacts from '../../services/YetiContacts';
import YetiTicketProvider from '../../services/YetiTickets';
import EmailSelector from '../EmailSelector/EmailSelector';
import AutoCompleteSelectForm from '../InputComponents/AutoCompleteSelectForm';
import LightEdgeHistoryItem from './LightEdgeHistoryItem';
import { TicketDataProvider, useTicketData } from './TicketDataProvider';
import TicketPriority from './TicketPriority';
import { PROVIDERS } from './constants';
import { downloadBase64, downloadBlob, prependServiceIdToDescription } from './utils';
import { getProviderConfig } from './ProviderConfig';

const MAX_ATTACHMENT_SIZE_MB = 4.4;

var moment = require('moment-timezone');

const ConnectriaResponseRating = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasRating = props.userRatings.filter((e) => e.ticket_history_id === props.commentId);
  const toggle = (rating) => {
    setIsOpen(!isOpen);
    //if closing the rating and there is a historical value check for it first
    setRating(rating == null ? (hasRating.length > 0 ? hasRating[0].rating : null) : rating);
  };
  useEffect(() => {
    if (hasRating.length > 0) {
      setRating(hasRating[0].rating);
      setComment(hasRating[0].comment);
    }
  }, [hasRating]);

  const doRating = async () => {
    let endpoint =
      hasRating.length > 0
        ? ConnectriaTicketsService.updateTicketCommentRating(hasRating[0].id, rating, comment)
        : ConnectriaTicketsService.createTicketCommentRating(props.commentId, rating, comment);
    setIsLoading(true);
    let result = await endpoint;
    setIsLoading(false);
    toggle(rating);
  };
  const deleteRating = async () => {
    setIsLoading(true);
    let result = await ConnectriaTicketsService.deleteTicketCommentRating(hasRating[0].id);
    toggle(0);
    setIsLoading(false);
  };
  return (
    <div
      className={'p2 row '}
      style={{
        backgroundColor: '#e9e9e9',
        paddingLeft: 12 + 'px',
        borderTop: 0.0625 + 'rem solid #d5d9dd',
      }}
    >
      <p className="w-100">
        Was the following response helpful? Click here to rate:
        <div className="parent">
          <span
            id="rating-1"
            onClick={() => toggle(1)}
            className={(rating >= 1 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-2"
            onClick={() => toggle(2)}
            className={(rating >= 2 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-3"
            onClick={() => toggle(3)}
            className={(rating >= 3 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-4"
            onClick={() => toggle(4)}
            className={(rating >= 4 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
          <span
            id="rating-5"
            onClick={() => toggle(5)}
            className={(rating >= 5 ? 'fas rated' : 'far') + ' fa-star comment-rating'}
          />
        </div>
        <Collapse isOpen={isOpen} className="pr-1">
          <Card>
            <CardBody>
              <Input
                type="textarea"
                rows="4"
                onChange={(e) => setComment(e.target.value)}
                className="w-100 form-control"
                value={comment}
                placeholder="Add a comment about the quality of support you received."
              />
            </CardBody>
            <CardFooter>
              <div className="text-right">
                {hasRating.length > 0 ? (
                  <span
                    style={{ lineHeight: 35.6 + 'px' }}
                    className="pull-left cursor"
                    onClick={() => {
                      if (window.confirm('Are you sure you wish to delete this rating?')) deleteRating();
                    }}
                  >
                    [ remove my rating ]
                  </span>
                ) : (
                  <></>
                )}
                <Button type="button" onClick={() => toggle(null)} className="btn-sm btn-danger">
                  Close
                </Button>
                <Button disabled={isLoading} type="button" onClick={() => doRating()} className="btn-sm">
                  Submit
                </Button>
              </div>
            </CardFooter>
          </Card>
        </Collapse>
      </p>
    </div>
  );
};

function HistoryItem(props) {
  const [stateApp, stateAppActions] = appState();

  const momentTzGuess = moment.tz.guess();
  const commentCreatedTs = props.comment.created;
  const created = moment(commentCreatedTs, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z');
  const customerId = props.ticket?.customer_id || 0;

  //if the ticket wasn't created by Connectria assume all commenters from Connectria are "left-side"
  if (customerId) {
    return (
      <div key={props.index} className={`mt-2 ${props.comment.user.customer_id > 0 ? 'text-left' : 'text-left'}`}>
        <div className="p-2 row" style={{ backgroundColor: '#e9e9e9', paddingBottom: 0 + 'px !important' }}>
          <div style={{ width: 90 + '%' }} className="col">
            <p className="  mb-0">
              Comment by:{' '}
              <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{createdBy(props.comment.user)}</span> of{' '}
              <span style={{ fontWeight: 700 }}>
                {props.comment.user.customer_id > 0 ? stateApp.userInfo.organization_name : 'Connectria'}
              </span>
            </p>

            <p className=" mb-0">{created}</p>
          </div>
          <div style={{ maxWidth: 10 + '%' }} className="col center-flex-item">
            {props.comment.user.customer_id == 0 ? (
              <img
                style={{ width: 30 + 'px' }}
                src="data:image/png;base64,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"
              />
            ) : (
              null
            )}
          </div>
        </div>
        {props.comment.user.customer_id == 0 ? (
          <ConnectriaResponseRating
            userRatings={props.userRatings}
            commentId={props.comment.id}
          />
        ) : (
          null
        )}
        <p className="pb-3 pt-1" style={{ fontSize: 0.97 + 'rem', whiteSpace: 'pre-wrap' }}>
          {props.comment.comment}
        </p>
      </div>
    );
  } else {
    //created by connectria. if comment comes from a different team assume it's "left-side"
    return (
      <div
        key={props.index}
        className={`mt-2 ${props.comment.user.primary_team_id !== props.ticket.team_id ? 'text-left' : 'text-left'}`}
      >
        <p>{props.comment.comment}</p>
        <blockquote className={`primary ${props.comment.user.primary_team_id !== props.ticket.team_id ? 'right' : ''}`}>
          {createdBy(props.comment.user)} - {moment(props.comment.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').calendar()}
        </blockquote>
      </div>
    );
  }
}

const TicketComments = ({ ticket }) => {
  const { comments, ratings, isLoadingComments, provider } = useTicketData();

  if (isLoadingComments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  const HistoryComponent = provider === PROVIDERS.CONNECTRIA ? HistoryItem : LightEdgeHistoryItem;

  return (
    <>
      {comments == null || comments.length === 0 ? (
        <div />
      ) : (
        comments.map((comment, index) => (
          <HistoryComponent
            userRatings={ratings}
            index={index}
            comment={comment}
            ticket={ticket}
            key={index}
          />
        ))
      )}
    </>
  );
};

const FileTypeIcon = ({ filename }) => {
  const extension = filename.split('.').pop().toLowerCase();
  switch (extension) {
    case 'pdf':
      return <i className="fa fa-file-pdf" />;
    case 'txt':
      return <i className="fa fa-file-alt" />;
    case 'gif':
    case 'jpg':
    case 'jpeg':
    case 'tif':
    case 'png':
      return <i className="fa fa-file-image" />;
    case 'doc':
    case 'docx':
    case 'docm':
      return <i className="fa fa-file-word" />;
    case 'xls':
    case 'xlsx':
    case 'xlsm':
      return <i className="fa fa-file-excel" />;
    default:
      return <i className="fa fa-file" />;
  }
};

const Attachment = (props) => {
  const { provider } = useTicketData();
  const [downloading, setDownloading] = useState(false);

  const download = async (e, file) => {
    e.preventDefault();
    setDownloading(true);

    try {
      if (provider === PROVIDERS.CONNECTRIA) {
        const attachmentUrl = await ConnectriaTicketsService.getAttachmentUrl(props.ticketId, file.id);
        window.open(attachmentUrl, '_blank');
      } else {
        const response = await YetiTicketProvider.getAttachment(file.id, props.ticketId);
        const filename = file.filename || 'downloaded_file';
        const mimeType = file.mimetype || 'application/octet-stream';
        //downloadBlob(response.data, filename, mimeType);
        downloadBase64(response.data, filename, mimeType);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <a
      onClick={(e) => {
        download(e, props.file);
      }}
    >
      <div className="attachment-block rounded">
        <FileTypeIcon filename={props.file.filename} className="attachment-icon" />
        <div>
          <h4>{props.file.filename}</h4>
          <small>{moment(props.file.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').calendar()}</small>
        </div>
        <div style={{ margin: 'auto' }}>{downloading ? <i className="fas fa-2x fa-spinner fa-spin" /> : <></>}</div>
      </div>
    </a>
  );
};

const BladeTabAttachments = ({ ticketId }) => {
  const { attachments, isLoadingAttachments } = useTicketData();

  if (isLoadingAttachments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  return (
    <>
      {!attachments || attachments.length === 0 ? (
        <div className="text-center mt-4 text-bold">
          <i className="fas fa-exclamation-triangle" /> No Attachments
        </div>
      ) : (
        <>
          {attachments.map((file) => (
            <Attachment ticketId={ticketId} file={file} key={ticketId} />
          ))}
        </>
      )}
    </>
  );
};

const TicketCloseForm = ({ id, reopen }) => {
  const [, stateAppActions] = appState();
  const [, stateTicketActions] = useTriaTicketsState();
  const [closeComment, setCloseComment] = useState('');
  const [closeRating, setCloseRating] = useState(6);
  const [isClosing, setIsClosing] = useState(false);
  const { revalidateTicketDetails } = useTicketData();

  const closeTicket = async (comment) => {
    await ConnectriaTicketsService.closeTicket(id, comment, closeRating);
    //remove ticket from state and update UI
    stateTicketActions.removeTicket(id);
    // after making removal call, revalidate ticket details and refresh.
    revalidateTicketDetails();
    stateAppActions.setAlert({
      content: <p style={{ color: 'white' }}>Ticket #{id} closed.</p>,
      className: 'success',
      visible: true,
    });
    setTimeout(() => {
      stateAppActions.setAlert((prevState) => ({ ...prevState, visible: false }));
    }, 2000);
  };

  const handleClose = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsClosing(true);
    try {
      if (reopen) {
        await ConnectriaTicketsService.reopenTicket(id, closeComment);
        //clear tickets to reset state
        stateTicketActions.setTicketStatus(id, 'Open');
        revalidateTicketDetails();
      } else {
        const comment = closeComment === '' ? 'Closed' : closeComment;
        await closeTicket(comment);
      }
    } catch (error) {
      console.log(error);
      stateAppActions.setBlade({
        visible: false,
        content: '',
        title: '',
      });
      stateAppActions.setAlert({
        content: <p style={{ color: 'white' }}>Error updating ticket.</p>,
        className: 'danger',
        visible: true,
      });
    }
  };

  return (
    <form className="mt-3" onSubmit={(e) => handleClose(e)}>
      <Label>
        <strong>Reason and Rating</strong>
      </Label>
      <p>
        How would you rate the support you received?{' '}
        {!reopen && (
          <span className="pl-2">
            <Button
              className={`btn-sm ${closeRating === 6 ? '' : 'btn-simple'}`}
              onClick={() => setCloseRating(6)}
              active={closeRating === 6}
              color="primary"
              style={{ border: '1px solid', borderColor: closeRating === 6 ? 'transparent' : '' }}
            >
              <i className="far fa-thumbs-up" />
            </Button>
            <Button
              className={`btn-sm ${closeRating === 3 ? '' : 'btn-simple'}`}
              onClick={() => setCloseRating(3)}
              active={closeRating === 3}
              color="primary"
              style={{ border: '1px solid', borderColor: closeRating === 3 ? 'transparent' : '' }}
            >
              <i className="far fa-thumbs-down" />
            </Button>
          </span>
        )}
      </p>
      <hr />
      <Input
        type="textarea"
        placeholder={
          reopen ? 'Re-Open Comment' : 'Please add a reason why this ticket is being closed (optional).'
        }
        onChange={(e) => setCloseComment(e.target.value)}
        rows="4"
        value={closeComment}
      />
      <div className="text-right mt-2">
        {isClosing ? (
          <Button type="submit" disabled={isClosing} className="btn-sm">
            <i className="fas fa-spinner fa-spin ml-1" style={{ fontSize: '15px' }} /> Submit
          </Button>
        ) : (
          <>
            {!reopen && (
              <Button type="submit" className="btn-sm">
                Submit
              </Button>
            )}
            {reopen && (
              <Button type="submit" disabled={closeComment.trim().split(/[\s\n]+/).length < 4} className="btn-sm">
                Submit
              </Button>
            )}
          </>
        )}
      </div>
    </form>
  );
};

const ConvertedDeviceId = (props) => {
  const [assetTag, setAssetTag] = useState(props.deviceId);
  const source = axios.CancelToken.source();

  useAsyncEffect(
    async (isMounted) => {
      try {
        var reg = new RegExp(/^\d+$/);

        if (reg.test(props.deviceId)) {
          let devices =
            (await ConnectriaTicketsService.getAllDevicesLike({
              ar_assoc: 'devicetype',
              limit: 100,
            })) || [];

          for (var i = 0; i < devices.length; i++) {
            if (devices[i].id === props.deviceId) setAssetTag(devices[i].assettag);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      }
    },
    () => {
      source.cancel();
    },
    []
  );

  return <span>{assetTag}</span>;
};

const CCForm = (props) => {
  return (
    <>
      <Label>
        <strong>Edit Ticket CC</strong>
      </Label>
      <div className="form-control" style={{ overflowY: 'scroll', height: 100 + 'px', minHeight: 100 + 'px' }}>
        {props.ccUsers.map((user, index) => (
          <div
            className={'d-flex w-100'}
            key={index}
            onClick={() => {
              props.handleCCSelect(user);
            }}
            style={{ backgroundColor: user.selected ? 'lightblue' : '', cursor: 'pointer' }}
          >
            <p style={{ flex: '0 50%' }}>{user.fname + ' ' + user.surname}</p>
            <p style={{ flex: '0 50%' }}>{'<' + user.email + '>'}</p>
          </div>
        ))}
      </div>
      {props.showCcCsm && (
        <div className="position-relative form-check ">
          <label className="form-check-label">
            <nobr>cc {props.csmInfo.user.fname + ' ' + props.csmInfo.user.surname}</nobr>
            <input
              type="checkbox"
              className="form-check-input"
              checked={props.includeCSM}
              onChange={props.handleCsmSelect}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
          </label>
        </div>
      )}
      <Button
        className="btn-sm"
        style={{ minHeight: 18 + 'px' }}
        onClick={() => {
          props.handleCCSave();
        }}
      >
        Save CC
      </Button>
      <div className="mb-3" />
    </>
  );
};

const EMAIL_REGEX = /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;

const LECCForm = ({ requesterId: preRequesterId, recipients = [], ticketId, setShowCCForm, detail }) => {
  const [requesterList, setRequesterList] = useState([]);
  const [requesterId, setRequesterId] = useState(preRequesterId);
  const [additionalRecipients, setAdditionalRecipients] = useState(recipients.map(recipient => recipient.email).join(','));
  const [additionalRecipientsInvalid, setAdditionalRecipientsInvalid] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [CCSaveLoader, setCCSaveLoader] = useState(false);
  const { revalidateTicketDetails } = useTicketData();

  const handleCCSave = async () => {
    try {
      const allRecipients = [...additionalRecipients.split(','), inputValue.trim()]
        .filter(Boolean) // remove empty values
        .filter((email, index, self) => self.indexOf(email) === index); // deduplicate

      const invalidEmails = allRecipients.filter(email => !EMAIL_REGEX.test(email));
      if (invalidEmails.length > 0) {
        setAdditionalRecipientsInvalid(true);
        return;
      }

      setCCSaveLoader(true);

      // const orgId = CookieService.getCookie('OrganizationId');
      // await YetiTicketProvider.updateRecipients(orgId, ticketId, allRecipients, detail);
      await YetiTicketProvider.updateRecipients(ticketId, allRecipients, detail);
      if (requesterId !== preRequesterId) {
        // await YetiTicketProvider.modifyTicketRequester(orgId, ticketId, requesterId);
        await YetiTicketProvider.modifyTicketRequester(ticketId, requesterId);
        const requsterDetail = requesterList.filter(detail => detail.id === requesterId)[0];
        detail.user.fname = requsterDetail.first_name;
        detail.user.surname = requsterDetail.last_name;
      }
      revalidateTicketDetails();
      setCCSaveLoader(false);
      setShowCCForm(false);
    } catch (error) {
      console.log('error', error);
      setCCSaveLoader(false);
    }
  }

  const handleAdditionalRecipientChange = (value) => {
    if (EMAIL_REGEX.test(value) || !value) {
      setAdditionalRecipientsInvalid(false);
    } else {
      setAdditionalRecipientsInvalid(true);
    }

    setAdditionalRecipients(value);
  };

  const existingRecipients = useMemo(() => recipients.map(recipient => recipient.email));


  useEffect(() => {
    (async () => {
      setCCSaveLoader(true);

      let employeeList = await YetiContacts.contact_list();
      setRequesterList(employeeList.data.results.map(employee => ({ ...employee, description: `${employee.first_name} ${employee.last_name} (${employee.email})` })))

      setCCSaveLoader(false);
    })();
  }, []);

  if (CCSaveLoader) {
    return (
      <>
        {CCSaveLoader && (
          <div className="text-center">
            <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }}></i>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <Label>
        <strong>Edit Ticket CC</strong>
      </Label>

      <Label className="font-weight-bold">Ticket Requester</Label>
      <AutoCompleteSelectForm
        id="ddTicketRequester"
        onChange={(e) => setRequesterId(e.target.value)}
        options={requesterList}
        value={requesterId}
      />
      <div className='pt-2'>
        <Label className="font-weight-bold" for="txtDescription">
          Additional Recipients
        </Label>
        <EmailSelector
          existingRecipients={existingRecipients}
          recipients={requesterList}
          onChange={handleAdditionalRecipientChange}
          onInputChange={setInputValue}
          setInvalidEmail={setAdditionalRecipientsInvalid}
        />
        {additionalRecipientsInvalid && (
          <span id="recipients-error" className="form-error">
            Invalid email address. Please ensure all email addresses are properly formatted (e.g., user@example.com)
          </span>
        )}
      </div>

      <Button
        className="btn-sm mt-3"
        style={{ minHeight: '18px' }}
        onClick={handleCCSave}
        disabled={additionalRecipientsInvalid}
        type='button'
      >
        Save CC
      </Button>
      <div className="mb-3"></div>
    </>
  );
}

const EditTicketDescription = (props) => {
  const [description, setDescription] = useState(props.detail?.issue);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { comments, provider } = useTicketData();

  if (!props.detail) return null;

  if (provider === PROVIDERS.LIGHTEDGE) {
    return (
      <div id="taDescription" className="mt-1 flex-grow-1 rounded mb-3">
        {comments.length > 0 ? comments[comments.length - 1].comment : ''}
      </div>
    );
  }

  if (['New', 'Open', 'WaitOnCust'].indexOf(props.detail?.status) === -1) {
    // show basic header without editing feature
    return (
      <div id="taDescription" className="mt-1 flex-grow-1 rounded mb-3">
        {props.detail?.issue}
      </div>
    );
  }

  const save = async (e) => {
    if (props.detail?.issue !== description) {
      setEditMode(false);
      setIsLoading(true);
      await ConnectriaTicketsService.updateTicket(props.detail?.id, { issue: description });
      setIsLoading(false);

      ConnectriaTicketsService.clearTicketCache();
      props.setReloadDetail(true);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  } else if (editMode) {
    return (
      <textarea
        value={description}
        className="form-control rounded"
        rows="10"
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        onBlur={(e) => save(e)}
      />
    );
  } else {
    return (
      <div id="taDescription" onClick={() => setEditMode(true)} className="mt-1 flex-grow-1 rounded mb-3">
        {props.detail.issue}
      </div>
    );
  }
};

const BladeTabDetail = (props) => {
  const source = axios.CancelToken.source();
  const [closeFormVisible] = useState(false);
  const [reopenFormVisible, setReopenFormVisible] = useState(false);
  const [showCCForm, setShowCCForm] = useState(false);
  const [showCCSaveLoader, setCCSaveLoader] = useState(false);
  const [ccUsers, setCCUsers] = useState([]);
  const [includeCSM, setIncludeCSM] = useState(false);
  const [csmInfo, setCsmInfo] = useState({});
  const [showCcCsm, setShowCcCsm] = useState(false);
  const [notifyCsmMeta, setNotifyCsmMeta] = useState({});
  const [stateApp] = appState();
  const { provider } = useTicketData();

  const momentTzGuess = moment.tz.guess();

  useAsyncEffect(
    async (isMounted) => {
      try {
        let isCustomer = stateApp.userInfo.organization_id !== 0;
        var users = await ConnectriaTicketsService.getAllUsersLike(
          {
            cancelToken: source.token,
          },
          isCustomer
        ) || [];
        let [accountAgents, metadata] = await Promise.all([
          ConnectriaTicketsService.getParamData({ cancelToken: source.token }),
          PortalService.getPortalCustomerMeta({ meta_key: 'tria_show_cc_csm' }),
        ]);
        users?.sort((a, b) => {
          return a.fname.localeCompare(b.fname);
        });
        let existsCCUsers = props.detail?.ticket_carbon_copies?.map((item) => {
          return item.user_id;
        }) || [];
        setCCUsers(
          users?.filter((user) => {
            user.selected = existsCCUsers.indexOf(user.id) > -1;
            return (
              user.active &&
              user.login_supported &&
              !user.serviceUser &&
              (props.detail.user.email !== stateApp.userInfo?.email || user.email !== stateApp.userInfo?.email)
            );
          })
        );

        accountAgents = accountAgents.dynamic.response.data.customer_account_agents || [];
        for (var agent of accountAgents) {
          if (agent.agent_type.name === 'Customer Success Manager') {
            setCsmInfo(agent);
          }
        }
        const notifyCsmTicketMeta = props.detail.meta?.find((meta) => meta.meta_key === 'notify_csm');
        if (metadata.meta_value === 'TRUE') {
          setShowCcCsm(true);
          if (notifyCsmTicketMeta) {
            setNotifyCsmMeta(notifyCsmTicketMeta);
            setIncludeCSM(true);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      }
    },
    () => {
      source.cancel();
    },
    []
  );

  const handleCsmSelect = () => {
    setIncludeCSM(!includeCSM);
  };

  const handleCCSave = async () => {
    setShowCCForm(false);
    setCCSaveLoader(true);

    let selected_users = ccUsers.filter((user) => {
      return user.selected;
    });
    let selected_user_ids = selected_users.map((user) => {
      return user.id;
    });

    // delete unselected users
    let users = [];
    for (let item of props.detail.ticket_carbon_copies) {
      if (selected_user_ids.indexOf(item.user_id) > -1) {
        users.push(item);
      } else {
        await ConnectriaTicketsService.deleteTicketCarbonCopy(item.id);
      }
    }
    props.detail.ticket_carbon_copies = users;

    // add new users
    let exists_user_ids = props.detail?.ticket_carbon_copies?.map((user) => {
      return user.user_id;
    }) || [];
    for (let user of selected_users) {
      if (exists_user_ids.indexOf(user.id) === -1) {
        let cc = await ConnectriaTicketsService.saveTicketCarbonCopies(props.detail.id, [user.id]);
        props.detail.ticket_carbon_copies.push(cc[0]);
      }
    }
    // update user objects by user_id
    await ConnectriaTicketsService.updateUsersTicketCarbonCopies(props.detail);
    if (includeCSM && Object.keys(notifyCsmMeta).length === 0) {
      var response = await ConnectriaTicketsService.createTicketMeta(props.detail.id, 'notify_csm', 1);
      setNotifyCsmMeta(response.ticket_meta[0]);
      // Leave the checkbox checked
      setIncludeCSM(true);
    } else if (!includeCSM && Object.keys(notifyCsmMeta).length !== 0) {
      await ConnectriaTicketsService.deleteTicketMeta(notifyCsmMeta.id);
      setNotifyCsmMeta({});
      // Leave the checkbox unchecked
      setIncludeCSM(false);
    }
    setCCSaveLoader(false);
  };

  const handleCCSelect = (selectedUser) => {
    let usersCopy = ccUsers;

    for (let user of usersCopy) {
      if (user.id === selectedUser.id) {

        user.selected = !user.selected;
      }
    }

    setCCUsers(usersCopy.slice());
  };
  const getCustomerTicketId = ({ detail }) => {
    if (!detail) return null;

    const curiumIdMeta = detail.meta?.find((metaItem) => metaItem.meta_key === 'curium_sys_id');

    if (!curiumIdMeta) return null;

    return (
      <tr>
        <td>Customer Ticket #:</td>
        <td>
          <span className="font-weight-bold">{curiumIdMeta.meta_value}</span>
        </td>
      </tr>
    );
  };

  if (!props.detail) {
    return null
  }

  let statusDisplay = props.detail.status === 'WaitOnCust' ? 'Input Required' : props.detail.status;
  if (provider === PROVIDERS.LIGHTEDGE ) {
    // Display mapped status or fallback if not set
    const providerConfig = getProviderConfig(provider);
    const lowerCaseStatus = String(props.detail.status || '').toLowerCase();
    statusDisplay = providerConfig.ticketStatusMap[lowerCaseStatus]?.label || props.detail.status;
  }

  return (
    <div id="paneDetails" className="flex-fill d-flex flex-column tab-pane">
      <blockquote className={`mt-3 blockquote ticket-${props.detail?.status}`}>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            <tr>
              <td>{provider === PROVIDERS.CONNECTRIA ? 'Owner:' : 'Requester: '}</td>
              <td>
                <span className="font-weight-bold">{createdBy(props.detail?.user)}</span>
              </td>
            </tr>
            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td>Created By:</td>
                <td>
                  <span className="font-weight-bold">{createdBy(props.detail?.enteredby)}</span>
                </td>
              </tr>
            )}
            <tr>
              <td>Created:</td>
              <td>
                <span className="font-weight-bold">
                  {props.detail?.created && moment(props.detail.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z')}
                </span>
              </td>
            </tr>

            {getCustomerTicketId({ detail: props.detail })}

            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td className="align-top">CC:</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>
                  <span className="font-weight-bold">
                    {(props.detail?.ticket_carbon_copies || [])
                      .map((item) => {
                        return ((item.user || {}).fname || '') + ' ' + ((item.user || {}).surname || '');
                      })
                      .join(', ')}
                  </span>
                  {(props.detail?.ticket_carbon_copies || []).length > 0 ? <br /> : null}
                  <Button href="#" onClick={() => setShowCCForm(!showCCForm)} className="btn-sm">
                    Edit CC
                  </Button>
                </td>
              </tr>
            )}
            {provider === PROVIDERS.LIGHTEDGE && (
              <tr>
                <td className="align-top">CC:</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>
                  {props.detail?.recipients?.length > 0
                    ? props.detail?.recipients.map((item) => (
                      <div key={item.email}>
                        <span className="font-weight-bold">{item.email}</span>
                        <br />
                      </div>))
                    : <span className="font-weight-bold">No Recipients</span>
                  }
                  <br />
                  {props.detail?.status?.toLowerCase() !== 'closed' && (
                    <Button href="#" onClick={() => setShowCCForm(!showCCForm)} className="btn-sm">
                      Edit CC
                    </Button>
                  )}
                </td>
              </tr>
            )}
            {provider === PROVIDERS.CONNECTRIA && <tr>
              <td>Type:</td>
              <td>
                <span className="font-weight-bold">
                  CASE
                </span>
              </td>
            </tr>}
            <tr>
              <td>Status:</td>
              <td>
                <span className="font-weight-bold">
                  {statusDisplay}
                </span>
              </td>
            </tr>
            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td>Category:</td>
                <td>
                  <span className="font-weight-bold">{props.detail.ticketcategory?.name}</span>
                </td>
              </tr>
            )}
            {provider === PROVIDERS.CONNECTRIA && (
              <tr>
                <td>Sub-Category:</td>
                <td>
                  <span className="font-weight-bold">{props.detail.ticketcategory?.subcategory}</span>
                </td>
              </tr>
            )}
            {Boolean(props.detail.device) ? (
              <tr>
                <td>{provider === PROVIDERS.CONNECTRIA ? "Device:" : "Service:"}</td>
                <td>
                  <span className="font-weight-bold">
                    {props.detail?.service?.description && provider === PROVIDERS.LIGHTEDGE && (
                      <span>{prependServiceIdToDescription(props.detail.service).description}</span>
                    )}
                    {provider === PROVIDERS.CONNECTRIA && (
                      <ConvertedDeviceId deviceId={props.detail.device} />
                    )}
                  </span>
                </td>
              </tr>
            ) : (
              <></>
            )}
          </tbody>
        </table>
      </blockquote>
      {showCCSaveLoader && (
        <>
          <div className="text-center">
            <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
          </div>
        </>
      )}
      {showCCForm && provider === PROVIDERS.CONNECTRIA && (
        <>
          <CCForm
            ccUsers={ccUsers}
            handleCCSave={handleCCSave}
            handleCCSelect={handleCCSelect}
            showCcCsm={showCcCsm}
            csmInfo={csmInfo}
            includeCSM={includeCSM}
            notifyCsmMeta={notifyCsmMeta}
            handleCsmSelect={handleCsmSelect}
          />
        </>
      )}
      {showCCForm && provider === PROVIDERS.LIGHTEDGE && (
        <LECCForm
          requesterId={props.detail.enteredBy}
          recipients={props.detail.recipients}
          ticketId={props.detail.id}
          setShowCCForm={setShowCCForm}
          detail={props.detail}
        />
      )}
      {provider === PROVIDERS.CONNECTRIA && <Label className="mt-1">
        <strong>Ticket Actions</strong>
      </Label>}
      {props.detail?.status?.toLowerCase() !== 'closed' ? (
        <div>
          {props.detail.status === 'Resolved' && (
            <>
              <Button
                className="btn-sm"
                id="toggleReopen"
                disabled={closeFormVisible}
                onClick={(e) => setReopenFormVisible(!reopenFormVisible)}
              >
                Re-Open Ticket
              </Button>
            </>
          )}

          {provider !== PROVIDERS.LIGHTEDGE && (
            <>
              <Button id="toggleClose" className="btn-sm" disabled={reopenFormVisible}>
                Close Ticket
              </Button>
              <UncontrolledCollapse toggler="#toggleClose">
                <TicketCloseForm
                  id={props.detail.id}
                  reopen={false}
                  fullPage={props.fullPage}
                />
              </UncontrolledCollapse>
            </>
          )}
          {props.detail.status === 'Resolved' && (
            <UncontrolledCollapse toggler="#toggleReopen">
              <TicketCloseForm
                id={props.detail.id}
                reopen={true}
                fullPage={props.fullPage}
              />
            </UncontrolledCollapse>
          )}
        </div>
      ) : (
        <div>
          <div className="alert alert-success w-auto">
            <i className="fas fa-check-circle" /> Ticket closed
          </div>
        </div>
      )}
      <>
        <Label className="mt-3">
          <strong>Description</strong>
        </Label>
        <EditTicketDescription detail={props.detail} setReloadDetail={props.setReloadDetail} />
      </>

      {props.detail?.errorDesc?.length > 0 ? (
        <>
          <Label className="mt-3">
            <strong>Error Description</strong>
          </Label>
          <div id="taDescription" style={{ whiteSpace: 'pre-wrap' }} className="mt-1 flex-grow-1 rounded mb-3">
            {props.detail.errorDesc}
          </div>
        </>
      ) : (
        <></>
      )}
      {(typeof props.showCommentsMsg === 'undefined' ? true : props.showCommentsMsg) && (
        <p>*You can view ticket history in the Comments tab above.</p>
      )}
    </div>
  );
};

const initialState = {
  isPostingComment: false,
  attachments: [],
  attachmentError: null,
  commentError: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_POSTING':
      return { ...state, isPostingComment: action.payload };
    case 'ADD_ATTACHMENT':
      return { ...state, attachments: [...state.attachments, action.payload] };
    case 'SET_ATTACHMENT_ERROR':
      return { ...state, attachmentError: action.payload };
    case 'SET_COMMENT_ERROR':
      return { ...state, commentError: action.payload };
    case 'RESET_ATTACHMENTS':
      return { ...state, attachments: [], attachmentError: null };
    default:
      return state;
  }
};
const BladeTabComments = ({ detail }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [stateApp] = appState();
  const [cacheBuster, setCacheBuster] = useState(true);
  const [newComment, setNewComment] = useState('');
  const {
    isLoadingComments,
    provider,
    revalidateAttachments,
    revalidateComments,
    revalidateTicketDetails,
  } = useTicketData();
  const contactId = stateApp?.userInfo?.external_id || null; // 'ACT-20008313';
  // console.log('------------------------- userInfo ConnectriaTicketsBlade', JSON.stringify(stateApp?.userInfo));

  const changeCommentText = useCallback((text) => {
    setNewComment(text);
    dispatch({ type: 'SET_ATTACHMENT_ERROR', payload: null });
  }, []);

  const addAttachment = useCallback((e) => {
    let file = e.target?.files?.[0];
    if (file) {
      // check 5mb limit for the file size
      if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) { // AWS seems to limit ~4.4mb because of total response size and encoding. It was previously assumed 5MB.
        dispatch({
          type: 'SET_ATTACHMENT_ERROR',
          payload: `"${file.name}" exceeds the ${MAX_ATTACHMENT_SIZE_MB}MB size limit.`,
        });
      } else {
        dispatch({ type: 'ADD_ATTACHMENT', payload: file });
        dispatch({ type: 'SET_ATTACHMENT_ERROR', payload: null });
      }
      e.target.value = '';
    }
  }, []);

  const handleNewComment = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      dispatch({ type: 'SET_POSTING', payload: true });
      dispatch({ type: 'SET_COMMENT_ERROR', payload: null });

      try {
        const ticketId = detail?.id;
        if (!ticketId) {
          throw new Error('Ticket ID is missing.');
        }

        const base64Attachments = await Promise.all(
          state.attachments.map(async (attachment) => {
            try {
              const content = await FileService.toBase64(attachment);
              return { name: attachment.name, content };
            } catch (err) {
              throw new Error(`Failed to process attachment: ${attachment.name}`);
            }
          })
        );

        switch (provider) {
          case PROVIDERS.CONNECTRIA:
            await ConnectriaTicketsService.newComment(ticketId, newComment);
            for (let attachment of base64Attachments) {
              await ConnectriaTicketsService.addAttachment(
                ticketId,
                attachment.name,
                attachment.content
              );
            }
            break;

          case PROVIDERS.LIGHTEDGE:
            // console.log('------------------------- userinfo createTicketComment', JSON.stringify(stateApp?.userInfo));
            if (!contactId) {
              throw new Error('Contact missing external_id.');
            }
            await YetiTicketProvider.createTicketComment(ticketId, contactId, newComment);
            for (let attachment of base64Attachments) {
              await YetiTicketProvider.createTicketAttachment(
                ticketId,
                contactId,
                attachment.name,
                attachment.name,
                attachment.content
              );
            }
            break;

          default:
            throw new Error('Unsupported provider.');
        }

        revalidateComments();

        if (state.attachments.length > 0) {
          revalidateTicketDetails();
          revalidateAttachments();
        } else {
          setCacheBuster(true);
        }

        setNewComment('');
        dispatch({ type: 'RESET_ATTACHMENTS' });
      } catch (err) {
        console.error('Error handling new comment:', err);
        dispatch({ type: 'SET_COMMENT_ERROR', payload: 'File upload failed. Please verify the file is valid.' });
        dispatch({ type: 'RESET_ATTACHMENTS' });
      } finally {
        dispatch({ type: 'SET_POSTING', payload: false });
      }
    },
    [detail, provider, state.attachments, newComment, contactId, revalidateComments, revalidateAttachments, revalidateTicketDetails]
  );

  if (isLoadingComments) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  return (
    <div id="paneComments" className="flex-fill d-flex flex-column tab-pane">
      {(detail?.status || '').toLowerCase() !== 'closed' && (
        <form onSubmit={handleNewComment}>
          <Label className="mt-3">
            <strong>New Comment</strong>
          </Label>
          <Input
            onChange={(e) => changeCommentText(e.target.value)}
            type="textarea"
            id="taNewComment"
            rows="4"
            value={newComment}
          />
          {state.attachments.map((attachment, i) => (
            <div key={i}>Attached file: {attachment.name}</div>
          ))}
          <Input
            type="file"
            id="fileNewComment"
            className="mt-2 d-inline-block"
            disabled={newComment.length < 3}
            onChange={addAttachment}
            style={{ width: '95px' }}
          />
          <span>(Max File Size: {MAX_ATTACHMENT_SIZE_MB}MB)</span>
          {state.attachmentError && <p className="mt-2 text-danger font-italic">{state.attachmentError}</p>}
          {state.commentError && <p className="mt-2 text-danger font-italic">{state.commentError}</p>}
          <div className="text-right mt-2">
            <Button
              className="btn-sm"
              type="submit"
              disabled={state.isPostingComment || newComment.length < 3}
            >
              {state.isPostingComment ? (
                <>
                  <i className="fas fa-spinner fa-spin ml-1" style={{ fontSize: '15px' }} /> Saving
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </form>
      )}
      <TicketComments
        ticket={detail}
        ticketId={detail.id}
        cacheBuster={cacheBuster}
        setCacheBuster={setCacheBuster}
      />
      <InitialCommentDescription ticket={detail} />
    </div>
  );
};

const BladeTabs = ({ fullPage, showCommentsMsg = true }) => {
  const [activeTab, setActiveTab] = useState(TABS_MAP.DETAILS);
  const {
    attachments,
    comments,
    isLoadingAttachments,
    isLoadingComments,
    ticketDetails,
  } = useTicketData();

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <>
      <ButtonGroup className="d-flex mb-2 justify-content-center" role="group">
        {TABS.map((label) => (
          <Button
            key={label}
            onClick={() => toggleTab(label)}
            active={activeTab === label}
            className={`btn-sm w-100 h-100 ${activeTab !== label ? 'btn-simple' : ''}`}
          >
            {label}
            {label === TABS_MAP.COMMENTS && (
              <Badge color="secondary" className="float-right">
                {isLoadingComments
                  ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '6px' }} />
                  : comments?.length || 0
                }
              </Badge>
            )}
            {label === TABS_MAP.FILES && (
              <Badge color="secondary" className="float-right">
                {isLoadingAttachments
                  ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '6px' }} />
                  : attachments?.length || 0
                }
              </Badge>
            )}
          </Button>
        ))}
      </ButtonGroup>

      {activeTab === TABS_MAP.DETAILS && (
        <BladeTabDetail
          detail={ticketDetails}
          showCommentsMsg={showCommentsMsg}
          fullPage={fullPage}
        />
      )}

      {activeTab === TABS_MAP.COMMENTS && (
        <BladeTabComments detail={ticketDetails} />
      )}

      {activeTab === TABS_MAP.FILES && (
        <div id="paneFiles" className="flex-fill d-flex flex-column tab-pane">
          <BladeTabAttachments ticket={ticketDetails} ticketId={ticketDetails.id} />
        </div>
      )}
    </>
  )
};

function isTicket(obj) {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.customer_id === 'number' &&
    typeof obj.enteredby === 'object' &&
    obj.enteredby !== null &&
    typeof obj.enteredby?.customer_id === 'number' &&
    typeof obj.created === 'string' &&
    typeof obj.issue === 'string'
  );
}

const InitialCommentDescription = ({ ticket }) => {
  const [stateApp] = appState();
  const momentTzGuess = moment.tz.guess();

  if (!isTicket(ticket)) return null;

  const customerId = ticket.enteredby?.customer_id || 0;

  return (
    <div className="mt-2 text-left">
      <div className="p-2 row" style={{ backgroundColor: '#e9e9e9', paddingBottom: 0 + 'px !important' }}>
        <div style={{ width: '90%' }} className="col">
          <p className="mb-0">
            Original Ticket created by:{' '}
            <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{createdBy(ticket.enteredby)}</span> of{' '}
            <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>
              {/* TODO: check this with Sean */}
              {customerId === 0 ? 'Connectria' : ticket.provider || stateApp.userInfo.organization_name}
            </span>
          </p>
          <p className=" mb-0">
            {moment(ticket.created, 'YYYY-MM-DDThh:mm:ss.SSSZ').tz(momentTzGuess).format('LLL z')}
          </p>
        </div>
        <div style={{ maxWidth: 10 + '%' }} className="col center-flex-item">
          {customerId === 0 ? (
            <img
              alt="Connectria Logo"
              style={{ width: '30px' }}
              src="data:image/png;base64,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"
            />
          ) : (
            <></>
          )}
        </div>
      </div>
      <p className="pb-3 pt-1" style={{ fontSize: 0.97 + 'rem', whiteSpace: 'pre-wrap' }}>
        <Label className="mt-1">
          <strong>Description:</strong>
        </Label>
        <div id="taDescription" style={{ whiteSpace: 'pre-wrap' }} className="mt-1 rounded mb-3">
          {ticket.issue}
        </div>
        {ticket.errorDesc?.length > 0 ? (
          <>
            <Label className="mt-1">
              <strong>Error Description:</strong>
            </Label>
            <div id="taDescription" style={{ whiteSpace: 'pre-wrap' }} className="mt-1 rounded mb-3">
              {ticket.errorDesc}
            </div>
          </>
        ) : (
          <></>
        )}
      </p>
    </div>
  );
};

const EditTicketSubject = ({ detail }) => {
  const [subject, setSubject] = useState(detail.subject);
  const [editMode, setEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { provider, revalidateTicketDetails } = useTicketData();
  const isNonEditableStatus = ['new', 'open', 'waitoncust'].indexOf(detail.status?.toLowerCase()) === -1;

  if (provider === PROVIDERS.LIGHTEDGE || isNonEditableStatus) {
    // show basic header without editing feature
    return <h4>{detail?.subject}</h4>;
  }

  const style = {
    border: 'solid 1px #ddd',
    borderRadius: '3px',
    padding: '5px',
    fontSize: '18px',
    marginBottom: '15px',
  };

  const save = async () => {
    if (detail.subject !== subject) {
      setEditMode(false);
      setIsUpdating(true);
      await ConnectriaTicketsService.updateTicket(detail.id, { subject: subject });
      setIsUpdating(false);

      ConnectriaTicketsService.clearTicketCache();
      revalidateTicketDetails();
    }
  };

  if (isUpdating) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (editMode) {
    return (
      <input
        value={subject}
        style={style}
        className="form-control"
        maxLength={255}
        onChange={(e) => {
          setSubject(e.target.value);
        }}
        onBlur={(e) => save(e)}
      />
    );
  }

  return (
    <h4 style={style} onClick={() => setEditMode(true)}>
      {detail.subject}
    </h4>
  );
};

const TABS_MAP = {
  COMMENTS: 'Comments',
  DETAILS: 'Details',
  FILES: 'Files',
};
const TABS = [TABS_MAP.DETAILS, TABS_MAP.COMMENTS, TABS_MAP.FILES];

const TicketBladeContainer = ({ id, ticketProvider }) => {
  const provider = ticketProvider || PROVIDERS.CONNECTRIA;
  return (
    <TicketDataProvider ticketId={id} provider={provider}>
      <ConnectriaTicketsDetailBlade />
    </TicketDataProvider>
  );
}

const ConnectriaTicketsDetailBlade = ({ fullPage = false }) => {
  const { ticketDetails, isLoadingTicketDetails, ticketDetailsErrors } = useTicketData();

  if (isLoadingTicketDetails) {
    return (
      <div className="text-center">
        <i className="fas fa-spinner m-3 mt-4 fa-spin" style={{ fontSize: '30px' }} />
      </div>
    );
  }

  if (ticketDetailsErrors) {
    return ticketDetailsErrors;
  }

  return (
    <div className="connectria-tickets-detail-blade h-100 d-flex flex-column">
      {isEmpty(ticketDetails) ? (
        <div className="text-center">
          <h4>Unable to retrieve ticket information</h4>
        </div>
      ) : (
        <>
          <EditTicketSubject detail={ticketDetails} />
          <BladeTabs fullPage={fullPage} />
        </>
      )}
    </div>
  );
};

const NewTabLink = (props) => {
  const { tooltipMessage, idx, className, style } = props;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [text, setText] = useState(tooltipMessage);
  const toggle = () => {
    setTooltipOpen(!tooltipOpen);
    // When hovering off of tooltip, reset the message
    if (!tooltipOpen) setText(tooltipMessage);
  };
  const openNewTab = (e) => {
    e.preventDefault();
    window.open(props.link, '_blank');
    e.stopPropagation();
    return false;
  };
  return (
    <span onClick={(e) => openNewTab(e)}>
      <i
        className={'fas clickable fa-external-link-alt cursor pt-1 pl-1' + (className ? className : '')}
        id={'TooltipExample' + (idx + 1 ? idx : '')} // Index is optional
        style={{ fontSize: '0.9rem', ...style }}
      />
      <Tooltip
        placement="right"
        target={'TooltipExample' + (idx + 1 ? idx : '')} // Index is optional
        isOpen={tooltipOpen}
        toggle={toggle}
        style={{ backgroundColor: 'black', color: 'white' }}
      >
        {text}
      </Tooltip>
    </span>
  );
};
export { BladeTabAttachments, BladeTabComments, BladeTabDetail, BladeTabs, ConnectriaTicketsDetailBlade, EditTicketSubject, NewTabLink, TicketBladeContainer };

